import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const AdminPaymentConfig = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_holder: '',
    qris_image_url: '',
    is_active: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      navigate('/admin/login');
      return;
    }
    
    loadConfigs();
  }, [navigate]);

  const loadConfigs = async () => {
    try {
      const response = await api.get('/admin/payment-configs');
      setConfigs(response.data);
    } catch (error) {
      toast.error('Gagal memuat data payment config');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingConfig) {
        await api.put(`/admin/payment-configs/${editingConfig.id}`, formData);
        toast.success('Config berhasil diupdate');
      } else {
        await api.post('/admin/payment-configs', formData);
        toast.success('Config berhasil ditambahkan');
      }
      setShowDialog(false);
      setEditingConfig(null);
      setFormData({ bank_name: '', account_number: '', account_holder: '', qris_image_url: '', is_active: true });
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan config');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      bank_name: config.bank_name,
      account_number: config.account_number,
      account_holder: config.account_holder,
      qris_image_url: config.qris_image_url,
      is_active: config.is_active
    });
    setShowDialog(true);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Yakin ingin menghapus config ini?')) return;

    try {
      await api.delete(`/admin/payment-configs/${configId}`);
      toast.success('Config berhasil dihapus');
      loadConfigs();
    } catch (error) {
      toast.error('Gagal menghapus config');
    }
  };

  const addToSumberDana = async (config) => {
    try {
      const bankName = String(config.bank_name || '').trim() || 'BANK';
      const accountNumber = String(config.account_number || '').trim() || '-';
      const accountHolder = String(config.account_holder || '').trim() || '-';
      const sourceName = bankName === 'QRIS' ? 'QRIS' : `${bankName} - ${accountNumber}`;

      const existingResp = await api.get('/admin/funding-sources');
      const existing = Array.isArray(existingResp.data) ? existingResp.data : [];
      const exists = existing.some(
        (s) =>
          String(s.type || '').toLowerCase() === 'bank' &&
          String(s.bank_name || '').trim().toLowerCase() === bankName.toLowerCase() &&
          String(s.account_number || '').trim() === accountNumber
      );
      if (exists) {
        toast.error('Sumber dana sudah ada');
        return;
      }

      await api.post('/admin/funding-sources', {
        type: 'bank',
        name: sourceName,
        bank_name: bankName,
        account_number: accountNumber,
        account_holder: accountHolder,
        opening_balance: 0,
        is_active: !!config.is_active,
      });
      toast.success('Berhasil ditambahkan ke Sumber Dana');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan ke Sumber Dana');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </Link>
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-2">Payment Config</h1>
              <p className="text-zinc-400">Kelola nomor rekening dan QRIS</p>
            </div>
            <button
              data-testid="add-payment-config-btn"
              onClick={() => {
                setEditingConfig(null);
                setFormData({ bank_name: '', account_number: '', account_holder: '', qris_image_url: '', is_active: true });
                setShowDialog(true);
              }}
              className="bg-primary text-black px-6 py-3 rounded-md font-bold uppercase tracking-wide hover:bg-primary-hover transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Tambah Config
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* QRIS Config */}
              {configs.filter(c => c.bank_name === 'QRIS').map((config) => (
                <div key={config.id} data-testid={`config-item-${config.id}`} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-8 h-8 text-primary" />
                      <div>
                        <h3 className="text-xl font-bold text-white">QRIS</h3>
                        <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-bold ${config.is_active ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-zinc-700 text-zinc-400'}`}>
                          {config.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {config.qris_image_url && (
                    <div className="bg-white p-4 rounded-lg mb-4 inline-block">
                      <img src={config.qris_image_url} alt="QRIS" className="w-48 h-48 object-contain" />
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-config-${config.id}`}
                      onClick={() => handleEdit(config)}
                      className="flex-1 bg-transparent border-2 border-primary text-primary hover:bg-primary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => addToSumberDana(config)}
                      className="flex-1 bg-transparent border-2 border-secondary text-secondary hover:bg-secondary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Wallet className="w-4 h-4" />
                      Sumber Dana
                    </button>
                    <button
                      data-testid={`delete-config-${config.id}`}
                      onClick={() => handleDelete(config.id)}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Hapus
                    </button>
                  </div>
                </div>
              ))}

              {/* Bank configs */}
              {configs.filter(c => c.bank_name !== 'QRIS').map((config) => (
                <div key={config.id} data-testid={`config-item-${config.id}`} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{config.bank_name}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${config.is_active ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-zinc-700 text-zinc-400'}`}>
                        {config.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div>
                      <div className="text-xs text-zinc-400">Nomor Rekening</div>
                      <div className="text-2xl font-mono font-bold text-primary">{config.account_number}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Atas Nama</div>
                      <div className="text-white font-bold">{config.account_holder}</div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-config-${config.id}`}
                      onClick={() => handleEdit(config)}
                      className="flex-1 bg-transparent border-2 border-primary text-primary hover:bg-primary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => addToSumberDana(config)}
                      className="flex-1 bg-transparent border-2 border-secondary text-secondary hover:bg-secondary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Wallet className="w-4 h-4" />
                      Sumber Dana
                    </button>
                    <button
                      data-testid={`delete-config-${config.id}`}
                      onClick={() => handleDelete(config.id)}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Hapus
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Dialog Form */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase">
              {editingConfig ? 'Edit Payment Config' : 'Tambah Payment Config'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Tipe (Bank Name)</label>
              <input
                data-testid="config-bank-input"
                type="text"
                required
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                placeholder="QRIS, BCA, Mandiri, dll"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Nomor Rekening</label>
              <input
                data-testid="config-account-input"
                type="text"
                required={formData.bank_name !== 'QRIS'}
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                placeholder="1234567890"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Atas Nama</label>
              <input
                data-testid="config-holder-input"
                type="text"
                required={formData.bank_name !== 'QRIS'}
                value={formData.account_holder}
                onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                placeholder="PT Zero Futsal Indonesia"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                {formData.bank_name === 'QRIS' ? 'URL QRIS Image' : 'URL QRIS (Opsional)'}
              </label>
              <input
                data-testid="config-qris-input"
                type="url"
                required={formData.bank_name === 'QRIS'}
                value={formData.qris_image_url}
                onChange={(e) => setFormData({ ...formData, qris_image_url: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                placeholder="https://example.com/qris.png"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                data-testid="config-active-checkbox"
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-zinc-700 bg-zinc-950 text-primary focus:ring-primary"
              />
              <label className="text-sm font-semibold text-zinc-300">Aktif</label>
            </div>
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Batal
              </Button>
              <button
                data-testid="submit-config-btn"
                type="submit"
                className="flex-1 bg-primary text-black hover:bg-primary-hover px-6 py-2 rounded-md font-bold uppercase transition-all"
              >
                {editingConfig ? 'Update' : 'Tambah'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentConfig;
